// Prose → HTML: CommonMark with GFM's four productions, then what instruction/1 adds to Markdown
// (reference/markdown.md). The block-level additions are read line by line before
// micromark sees the text — footnote definitions (rendered once, at the end of the document),
// definition lists, `$$` display math, and the attribute lists of ATX headings and code fences —
// and the inline forms are micromark tokens (@instruction-md/spec's syntax), drawn
// here: typed references as object pills, citations, annotations, wiki-links and embeds, footnote
// references, math and parameters. Raw HTML is shown as text and dangerous protocols are dropped,
// as everywhere in the renderer.
import { compile, parse, postprocess, preprocess } from "micromark";
import { gfmAutolinkLiteral, gfmAutolinkLiteralHtml } from "micromark-extension-gfm-autolink-literal";
import { gfmStrikethrough, gfmStrikethroughHtml } from "micromark-extension-gfm-strikethrough";
import { gfmTable, gfmTableHtml } from "micromark-extension-gfm-table";
import { gfmTaskListItem, gfmTaskListItemHtml } from "micromark-extension-gfm-task-list-item";
import type { CompileContext, Event, HtmlExtension, Token } from "micromark-util-types";
import {
  INLINE, PATTERNS, inlineAttrSpec, parseAttributes, resolveTarget, splitWiki, syntaxExtensions,
  type Attrs, type ReadResult,
} from "@instruction-md/spec";
import { escapeHtml as e, taskLabels, unescapeHtml, visibleText, type Heading } from "./md";
import { sanitizeSvg } from "./figures";
import { ICON_PATHS, LUCIDE } from "./icons";

/** The media kinds' icons, and the common ones an :icon[…] names, in Lucide's hand like the rules' (lucide.dev, ISC; see NOTICE). */
const MEDIA: Record<string, string> = {
  image: '<rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>', // image
  video: '<path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/><rect x="2" y="6" width="14" height="12" rx="2"/>', // video
  audio: '<path d="M2 10v3"/><path d="M6 6v11"/><path d="M10 3v18"/><path d="M14 8v7"/><path d="M18 5v13"/><path d="M22 10v3"/>', // audio-lines
  pdf: '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/>', // file-text
  drawing: '<path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/>', // pen-tool
  resource: '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/>', // file
  calendar: '<path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/>',
  clock: '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>',
  mail: '<rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>',
  user: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  link: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
  lock: '<rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
  globe: '<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>',
  search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
};

/** A drawn icon by name, or nothing when there is none by that name. */
export const glyph = (name: string): string => LUCIDE[name] || MEDIA[name]
  ? `<svg class="lu" viewBox="0 0 24 24" aria-hidden="true">${LUCIDE[name] ?? MEDIA[name]}</svg>`
  : ICON_PATHS[name] ? `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="${ICON_PATHS[name]}"/></svg>` : "";

/** What prose needs from its document: identities to resolve references, declarations, the record's sources.
 *  Its diagnostics are the specification package's (check()); prose only draws what they describe. */
export interface ProseScope {
  read: ReadResult;
  allowImages: boolean;
  headingOffset: number;
  /** Ids already given to elements; generated anchors avoid them (§11.2). */
  used: Set<string>;
  headings: Heading[];
  /** Footnotes by first reference: label → number. */
  noteOrder: Map<string, number>;
  /** Footnote definitions: label → their Markdown, gathered from every prose segment. */
  notes: Map<string, string>;
  /** Link reference definitions from the whole document, so a reference resolves across segments. */
  definitions: string;
  /** The backmatter's source records, by id, for :cite. */
  sources: Map<string, Record<string, unknown>>;
  /** Parameter values to draw where `${name}` stands (RenderOptions.showValues); unset, the token itself. */
  values?: Record<string, unknown>;
}

/** A parameter's value as text, as the agent's text writes it (§12: a list joined by commas). */
export const shownValue = (v: unknown) => (Array.isArray(v) ? v.join(", ") : String(v));

const ICONS: Record<string, string> = { person: "identity", role: "identity", skill: "book", mcp: "infra", tool: "compute", agent: "core", service: "interface", source: "knowledge", resource: "resource", ref: "compose" };
const KIND_LABEL: Record<string, string> = { person: "Person", role: "Role", skill: "Skill", mcp: "MCP server", tool: "Tool", agent: "Agent", service: "Service", source: "Source", resource: "Resource" };
const IMAGE = /\.(?:png|jpe?g|gif|webp|avif|svg)(?:[?#].*)?$/i;

/** Ids the document claims for itself, which a generated anchor must not take. */
export function explicitIds(read: ReadResult): Set<string> {
  return new Set(read.identities.keys());
}

// ---- URLs ----

/** A link target a reader may follow: web and mail addresses, fragments and relative paths. */
export function safeHref(url: string): string | null {
  const u = url.trim();
  if (!u) return null;
  if (u.startsWith("#") || u.startsWith("/") || u.startsWith("./") || u.startsWith("../")) return u;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(u);
  if (!scheme) return u;
  return /^(?:https?|mailto)$/i.test(scheme[1]) ? u : null;
}

/** An image source that may be drawn: web addresses, relative paths and embedded raster or SVG data. */
export function safeImage(src: string): string | null {
  if (/^data:image\/(?:png|jpe?g|gif|webp|avif|svg\+xml)[;,]/i.test(src)) return src;
  const h = safeHref(src);
  return h && !h.startsWith("mailto:") && !h.startsWith("#") ? h : null;
}

// ---- §11.2 generated heading anchors ----

export function anchor(text: string, used: Set<string>): string {
  const base = text.toLowerCase().replace(/\s+/gu, "-").replace(/[^\p{L}\p{N}_-]/gu, "").replace(/^-+|-+$/g, "") || "section";
  let id = base;
  for (let n = 2; used.has(id); n++) id = `${base}-${n}`;
  used.add(id);
  return id;
}

// ---- the line pre-pass ----

interface DefEntry { term: string; defs: string[] }
type Part = { kind: "md"; text: string } | { kind: "dl"; entries: DefEntry[] };

const FENCE = /^([ \t]*(?:>[ \t]?)*[ \t]*)(`{3,}|~{3,})(.*)$/;
const FOOTNOTE = /^ {0,3}\[\^([A-Za-z0-9][A-Za-z0-9._-]*)\]:[ \t]?(.*)$/;
const ATX_ATTRS = /^( {0,3}#{1,6}[ \t].*?)[ \t]+(\{[^{}]*\})[ \t]*$/;
const DEF_OPEN = /^:[ \t]+(.*)$/;
const MATH = /^\$\$[ \t]*$/;
const BLOCK_START = /^ {0,3}(?:#{1,6}(?:[ \t]|$)|>|[-*+][ \t]|\d{1,9}[.)][ \t]|`{3,}|~{3,}|\||<)/;
const HEADING_MARK = /\uE000(\d+)\uE001/;
const CODE_MARK = /\uE002(\d+)\uE003/;

/** What the pre-pass took out of the text for micromark, by the index its private-use mark carries. */
interface Marks { headings: Attrs[]; fences: Attrs[] }

function prepass(source: string, scope: ProseScope, marks: Marks): Part[] {
  const lines = source.replace(/\n$/, "").split("\n");
  const parts: Part[] = [];
  let md: string[] = [];
  let fence: { char: string; len: number } | null = null;
  const flush = () => { if (md.length) parts.push({ kind: "md", text: md.join("\n") + "\n" }); md = []; };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (fence) {
      md.push(line);
      const close = /^[ \t]*(?:>[ \t]?)*[ \t]*(`+|~+)[ \t]*$/.exec(line);
      if (close && close[1][0] === fence.char && close[1].length >= fence.len) fence = null;
      continue;
    }
    const f = FENCE.exec(line);
    if (f && !(f[2][0] === "`" && f[3].includes("`"))) {
      fence = { char: f[2][0], len: f[2].length };
      const info = f[3].trim();
      const { lang, attrs } = fenceInfo(info);
      if (attrs) {
        marks.fences.push(attrs);
        md.push(`${f[1]}${f[2]}${lang}\uE002${marks.fences.length - 1}\uE003`);
      } else md.push(line);
      continue;
    }
    // display math: $$ … $$, drawn as a math fence
    if (MATH.test(line)) {
      const end = lines.findIndex((l, j) => j > i && MATH.test(l));
      if (end > 0) {
        const body = lines.slice(i + 1, end).join("\n");
        const ticks = "`".repeat(Math.max(3, ...(body.match(/`+/g) ?? []).map((r) => r.length + 1)));
        md.push(`${ticks}math`, ...lines.slice(i + 1, end), ticks);
        i = end;
        continue;
      }
    }
    const note = FOOTNOTE.exec(line);
    if (note) {
      // the definition and its continuation (indented four spaces; blank lines may separate blocks)
      const body = [note[2]];
      let j = i + 1;
      for (; j < lines.length; j++) {
        if (/^(?: {4}|\t)/.test(lines[j])) body.push(lines[j].replace(/^(?: {4}|\t)/, ""));
        else if (!lines[j].trim() && j + 1 < lines.length && /^(?: {4}|\t)/.test(lines[j + 1])) body.push("");
        else break;
      }
      if (!scope.notes.has(note[1])) scope.notes.set(note[1], body.join("\n"));
      i = j - 1;
      continue;
    }
    // a definition list: a term line, then a line opening with ":" and a space
    if (line.trim() && !BLOCK_START.test(line) && !/^:/.test(line) && DEF_OPEN.test(lines[i + 1] ?? "") && !/^:{2,}/.test(lines[i + 1])) {
      const entries: DefEntry[] = [];
      let j = i;
      while (j < lines.length && lines[j].trim() && !BLOCK_START.test(lines[j]) && DEF_OPEN.test(lines[j + 1] ?? "") && !/^:{2,}/.test(lines[j + 1])) {
        const entry: DefEntry = { term: lines[j], defs: [] };
        j++;
        while (j < lines.length) {
          const d = DEF_OPEN.exec(lines[j]);
          if (!d || /^:{2,}/.test(lines[j])) break;
          const def = [d[1]];
          j++;
          for (; j < lines.length; j++) {
            if (/^(?: {4}|\t)/.test(lines[j])) def.push(lines[j].replace(/^(?: {4}|\t)/, ""));
            else if (!lines[j].trim() && /^(?: {4}|\t)/.test(lines[j + 1] ?? "")) def.push("");
            else break;
          }
          entry.defs.push(def.join("\n"));
        }
        entries.push(entry);
        while (j < lines.length && !lines[j].trim() && lines[j + 1]?.trim() && DEF_OPEN.test(lines[j + 2] ?? "")) j++;
      }
      flush();
      parts.push({ kind: "dl", entries });
      i = j - 1;
      continue;
    }
    const h = ATX_ATTRS.exec(line);
    if (h) {
      const parsed = parseAttributes(h[2].slice(1, -1), (k) => (k === "id" || k === "class" || k === "style" ? (k === "class" ? { type: "array" } : { type: "string" }) : undefined));
      if (!parsed.problems.length) {
        // closing # markers come before the attribute list; the mark goes before them
        marks.headings.push(parsed.attrs);
        md.push(`${h[1].replace(/[ \t]+#+$/, "")}\uE000${marks.headings.length - 1}\uE001`);
        continue;
      }
    }
    md.push(line);
  }
  flush();
  return parts;
}

// ---- inline forms ----

interface Open { kind: string; attrs: string | null }

function inlineHtml(scope: ProseScope): HtmlExtension {
  const open: Open[] = [];
  return {
    enter: {
      imdDirective(this: CompileContext) { this.buffer(); open.push({ kind: "", attrs: null }); },
    },
    exit: {
      imdDirectiveName(this: CompileContext, token: Token) { open[open.length - 1].kind = this.sliceSerialize(token); },
      imdDirectiveAttributes(this: CompileContext, token: Token) { open[open.length - 1].attrs = this.sliceSerialize(token).slice(1, -1); },
      imdDirective(this: CompileContext, token: Token) {
        const label = this.resume();
        const d = open.pop()!;
        this.raw(directiveHtml(d, label, this.sliceSerialize(token), scope));
      },
      imdWiki(this: CompileContext, token: Token) { this.raw(wikiHtml(this.sliceSerialize(token), false, (token as Token & { _inCell?: boolean })._inCell === true, scope)); },
      imdEmbed(this: CompileContext, token: Token) { this.raw(wikiHtml(this.sliceSerialize(token), true, (token as Token & { _inCell?: boolean })._inCell === true, scope)); },
      imdFootnoteRef(this: CompileContext, token: Token) {
        const raw = this.sliceSerialize(token);
        const label = raw.slice(2, -1);
        if (!scope.notes.has(label) && !scope.read.footnotes.has(label)) { this.raw(e(raw)); return; }
        let n = scope.noteOrder.get(label);
        const first = n === undefined;
        if (first) { n = scope.noteOrder.size + 1; scope.noteOrder.set(label, n); }
        this.raw(`<sup class="fnref" part="footnote-ref"><a href="#fn-${e(label)}"${first ? ` id="fnref-${e(label)}"` : ""} aria-label="Footnote ${n}">${n}</a></sup>`);
      },
      imdMath(this: CompileContext, token: Token) {
        const formula = this.sliceSerialize(token).slice(1, -1);
        this.raw(`<span class="math" part="math" data-math="inline" role="math" aria-label="${e(formula)}">${e(formula)}</span>`);
      },
      imdParam(this: CompileContext, token: Token) {
        const name = this.sliceSerialize(token).slice(2, -1);
        const p = scope.read.parameters.get(name);
        const hint = p ? [`parameter ${name}`, p.type, p.default !== undefined ? `default ${String(p.default)}` : "", p.required ? "required" : "", p.description ?? ""].filter(Boolean).join(" · ") : `undeclared parameter ${name}`;
        const v = scope.values?.[name];
        if (v !== undefined) { this.raw(`<span class="imd-param set" part="param" title="${e(`\${${name}} · ${hint}`)}">${e(shownValue(v))}</span>`); return; }
        this.raw(`<span class="imd-param${p ? "" : " unresolved"}" part="param" title="${e(hint)}">${e(`\${${name}}`)}</span>`);
      },
    },
  };
}

function inlineAttrs(kind: string, raw: string | null): Attrs {
  if (raw === null) return {};
  const known = kind in INLINE;
  return parseAttributes(raw, (k) => (known ? inlineAttrSpec(kind, k) : undefined), known).attrs;
}

function classes(a: Attrs, base: string): string {
  const extra = Array.isArray(a.class) ? a.class.filter((c) => /^[A-Za-z0-9_-]+$/.test(c)) : [];
  return [base, ...extra].join(" ");
}
const styleToken = (a: Attrs) => (typeof a.style === "string" && /^[A-Za-z0-9_-]+$/.test(a.style) ? ` data-style="${e(a.style)}"` : "");

function directiveHtml(d: Open, label: string, raw: string, scope: ProseScope): string {
  const a = inlineAttrs(d.kind, d.attrs);
  if (d.kind.includes(":")) return `<span class="${classes(a, "imd-ext")}" data-kind="${e(d.kind)}"${styleToken(a)} title="${e(`${d.kind}: an extension's inline object, shown as its label`)}">${label}</span>`;
  if (!(d.kind in INLINE)) return e(raw);
  switch (d.kind) {
    case "mark": return `<mark class="${classes(a, "imd-mark")}"${styleToken(a)}>${label}</mark>`;
    case "sub": return `<sub>${label}</sub>`;
    case "sup": return `<sup>${label}</sup>`;
    case "kbd": return `<kbd class="${classes(a, "imd-kbd")}">${label}</kbd>`;
    case "abbr": return `<abbr class="${classes(a, "imd-abbr")}" title="${e(String(a.title ?? ""))}">${label}</abbr>`;
    case "badge": return `<span class="${classes(a, "badge imd-badge")}"${styleToken(a)}>${label}</span>`;
    case "icon": {
      const name = label.replace(/<[^>]+>/g, "").trim();
      return `<span class="${classes(a, "imd-icon")}" role="img" aria-label="${e(name)}" title="${e(name)}">${glyph(ICON_ALIASES[name] ?? name)}<span class="imd-icon-name">${label}</span></span>`;
    }
    case "button": {
      const href = typeof a.href === "string" ? safeHref(a.href) : null;
      return href ? `<a class="${classes(a, "imd-button")}" href="${e(href)}"${external(href)}>${label}</a>` : `<span class="${classes(a, "imd-button")}">${label}</span>`;
    }
    case "cite": {
      const ref = typeof a.ref === "string" ? a.ref : "";
      const src = scope.sources.get(ref);
      return src ? `<cite class="imd-cite"><a href="#source-${e(ref)}" title="${e(String(src.title ?? ref))}">${label}</a></cite>` : `<cite class="imd-cite unresolved" title="unresolved citation ${e(ref)}">${label}</cite>`;
    }
    default: return referenceHtml(d.kind, a, label, scope);
  }
}

const ICON_ALIASES: Record<string, string> = { shield: "guardrail", star: "important", alert: "warning", bulb: "tip" };
const external = (href: string) => (/^https?:/i.test(href) ? ' target="_blank" rel="noopener noreferrer"' : "");

/** A typed reference (§11.1): a pill to the declaration it names, marked when it names nothing or the wrong kind. */
function referenceHtml(kind: string, a: Attrs, label: string, scope: ProseScope): string {
  const ref = typeof a.ref === "string" ? a.ref : "";
  let href: string | null = null;
  let state = "";
  let detail = KIND_LABEL[kind] ?? "Reference";
  if (ref) {
    const t = resolveTarget(scope.read, kind, ref);
    if (t.state === "missing") state = " unresolved";
    else if (t.state === "local") { href = `#${t.id}`; detail = `${detail} · #${t.id}`; if (!t.matches) state = " mismatched"; }
    else if (ref.startsWith("#")) { detail = `${detail} · ${ref} (in an included document)`; state = " external"; }
    else { href = safeHref(t.uri); detail = `${detail} · ${t.uri}`; state = " external"; }
  }
  const mark = kind === "person" ? `<span class="av" aria-hidden="true">${e(label.replace(/<[^>]+>/g, "").trim().charAt(0).toUpperCase() || "?")}</span>` : glyph(ICONS[kind] ?? "compose");
  const body = `${mark}<span class="obj-label">${label}</span>`;
  const cls = `${classes(a, "imd-obj")}${state}`;
  return href
    ? `<a class="${cls}" part="reference" data-kind="${e(kind)}" href="${e(href)}" title="${e(detail)}"${external(href)}>${body}</a>`
    : `<span class="${cls}" part="reference" data-kind="${e(kind)}" title="${e(detail)}">${body}</span>`;
}

function wikiHtml(raw: string, embed: boolean, inCell: boolean, scope: ProseScope): string {
  const inner = raw.slice(embed ? 3 : 2, -2);
  const { target, label } = splitWiki(inCell ? inner.replace(/\\\|/g, "|") : inner);
  const text = e(label ?? target);
  // a bare name is first a key of the front matter's references, then a relative path (§11.2)
  const named = (scope.read.document.metadata.front.references as Record<string, { uri?: unknown } | undefined> | undefined)?.[target];
  const to = typeof named?.uri === "string" ? named.uri : target;
  const href = safeHref(to.startsWith("#") || /^[a-z][a-z0-9+.-]*:/i.test(to) || to.startsWith(".") || to.startsWith("/") ? to : `./${to}`);
  if (embed) {
    const img = scope.allowImages && IMAGE.test(target) ? safeImage(target) : null;
    if (img) return `<img class="imd-embed-img" src="${e(img)}" alt="${text}">`;
    return `<span class="imd-embed" part="embed">${glyph(IMAGE.test(target) ? "image" : /\.pdf(?:[?#].*)?$/i.test(target) ? "pdf" : "resource")}${href ? `<a href="${e(href)}"${external(href)}>${text}</a>` : `<span>${text}</span>`}<span class="sub">embed</span></span>`;
  }
  return href ? `<a class="imd-wiki" href="${e(href)}"${external(href)}>${text}</a>` : text;
}

// ---- code fences: filename, caption, highlighted lines; figures; an html fence's page ----

const FENCE_ATTRS: Record<string, { type: "string" } | { type: "boolean" }> = { id: { type: "string" }, title: { type: "string" }, filename: { type: "string" }, highlight: { type: "string" }, caption: { type: "string" }, preview: { type: "boolean" } };

/** A fence's info string: its language (the first token) and its attribute list, when it has one (§9). */
export function fenceInfo(info: string): { lang: string; attrs?: Attrs } {
  const m = /^(\S*?)(?:[ \t]*(\{.*\}))?[ \t]*$/.exec(info.trim()) ?? /^(\S*)/.exec(info.trim())!;
  return { lang: m[1] ?? "", attrs: m[2] ? parseAttributes(m[2].slice(1, -1), (k) => FENCE_ATTRS[k]).attrs : undefined };
}

/** What a fence is called where it is a tab (§8): its title, else its filename, else its language. */
export function fenceTitle(info: string): string {
  const { lang, attrs = {} } = fenceInfo(info);
  return (typeof attrs.title === "string" && attrs.title) || (typeof attrs.filename === "string" && attrs.filename) || lang || "Code";
}

/** An html fence drawn as the page it describes (§9), as a tab beside its code. The frame is isolated: sandboxed
 *  without scripts, forms or same-origin, so it is an opaque origin that cannot reach the host page, its storage or
 *  its credentials; its own policy loads nothing but images, media, styles and fonts, and a link opens outside. */
function htmlView(text: string, pre: string, name: string): string {
  const lines = text.replace(/\n$/, "").split("\n").length;
  const size = lines <= 8 ? "s" : lines <= 30 ? "m" : "l";
  const page = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data:; style-src 'unsafe-inline' https:"><meta name="referrer" content="no-referrer"><base target="_blank"><style>body{margin:16px;font:14px/1.5 system-ui}</style></head><body>${text}</body></html>`;
  const frame = `<div class="html-frame" data-size="${size}"><iframe part="html-frame" title="${e(name ? `${name}, rendered` : "The HTML, rendered")}" sandbox="allow-popups allow-popups-to-escape-sandbox" referrerpolicy="no-referrer" srcdoc="${e(page)}"></iframe></div>`;
  const tab = (label: string, on: boolean) => `<button type="button" role="tab" aria-selected="${on}" tabindex="${on ? 0 : -1}">${label}</button>`;
  return `<div part="tabs" class="tabs html-view" data-tabs><div class="tablist" role="tablist" hidden>${tab("Rendered", true)}${tab("Code", false)}</div>`
    + `<section part="tab" class="tab-panel"><div class="tab-t">Rendered</div>${frame}</section><section part="tab" class="tab-panel"><div class="tab-t">Code</div>${pre}</section></div>`;
}

function highlightLines(spec: string): Set<number> {
  const out = new Set<number>();
  for (const part of spec.split(",").map((s) => s.trim())) {
    const m = /^(\d+)(?:-(\d+))?$/.exec(part);
    if (!m) continue;
    const a = Number(m[1]), b = Number(m[2] ?? m[1]);
    for (let n = a; n <= b && n - a < 10_000; n++) out.add(n);
  }
  return out;
}

function codeHtml(lang: string, code: string, attrs: Attrs): string {
  const text = unescapeHtml(code);
  const caption = typeof attrs.caption === "string" ? attrs.caption : "";
  const id = typeof attrs.id === "string" && PATTERNS.id.test(attrs.id) ? attrs.id : "";
  const idAttr = id ? ` id="${e(id)}"` : "";
  if (lang === "mermaid") {
    const first = /^\s*(?:%%\s*(.+)|---\s*\ntitle:\s*(.+))/.exec(text);
    const label = caption || (first?.[1] ?? first?.[2] ?? "").trim();
    return `<figure class="fig" data-fig="mermaid"${label ? ` aria-label="${e(label)}"` : ""}${idAttr} part="figure"><pre class="fig-src"><code>${code}</code></pre>${caption ? `<figcaption>${e(caption)}</figcaption>` : ""}</figure>`;
  }
  if (lang === "svg") {
    const s = sanitizeSvg(text);
    if (s) {
      const label = caption || s.title;
      return `<figure class="fig" data-fig="sketch"${label ? ` aria-label="${e(label)}"` : ""}${idAttr} part="figure">${s.svg}${label ? `<figcaption>${e(label)}</figcaption>` : ""}</figure>`;
    }
  }
  if (lang === "math") {
    return `<div class="math math-display" part="math" data-math="display" role="math" aria-label="${e(text.trim())}"${idAttr}>${e(text.replace(/\n$/, ""))}</div>`;
  }
  const head = typeof attrs.filename === "string" ? attrs.filename : typeof attrs.title === "string" ? attrs.title : "";
  const marked = typeof attrs.highlight === "string" ? highlightLines(attrs.highlight) : new Set<number>();
  const body = marked.size
    ? code.replace(/\n$/, "").split("\n").map((l, i) => (marked.has(i + 1) ? `<span class="hl">${l}</span>` : l)).join("\n") + "\n"
    : code;
  const pre = `<pre tabindex="0"><code${lang ? ` class="language-${e(lang)}"` : ""}>${body}</code></pre>`;
  const page = lang === "html" && attrs.preview !== false && text.trim() !== "";
  if (!head && !caption && !id && !page) return pre;
  return `<figure class="code${page ? " page" : ""}" part="code"${idAttr}>${head || lang ? `<div class="code-head"><span class="code-name">${e(head || lang)}</span>${head && lang ? `<span class="sub">${e(lang)}</span>` : ""}</div>` : ""}${page ? htmlView(text, pre, head || caption) : pre}${caption ? `<figcaption class="code-cap">${e(caption)}</figcaption>` : ""}</figure>`;
}

// ---- the segment ----

export interface ProseOptions {
  /** An example's or a verbatim body's text: base Markdown only, no Instruction forms (§6.2). */
  literal?: boolean;
  /** Draw headings without ids or outline entries (a definition, a label). */
  inline?: boolean;
}

/** One prose segment as HTML. */
export function proseHtml(source: string, scope: ProseScope, o: ProseOptions = {}): string {
  const marks: Marks = { headings: [], fences: [] };
  const parts = o.literal ? [{ kind: "md" as const, text: source }] : prepass(source, scope, marks);
  return parts.map((p) => (p.kind === "md" ? markdownHtml(p.text, scope, marks, o) : dlHtml(p.entries, scope, o))).join("");
}

function dlHtml(entries: DefEntry[], scope: ProseScope, o: ProseOptions): string {
  return `<dl class="defs" part="definitions">${entries.map((en) => `<div><dt>${inlineOnly(en.term, scope, o)}</dt>${en.defs.map((d) => `<dd>${proseHtml(d, scope, { ...o, inline: true })}</dd>`).join("")}</div>`).join("")}</dl>`;
}

/** A line of inline Markdown, without the paragraph around it. */
export function inlineOnly(text: string, scope: ProseScope, o: ProseOptions = {}): string {
  return markdownHtml(text, scope, { headings: [], fences: [] }, { ...o, inline: true }).replace(/^<p>|<\/p>\n?$/g, "");
}

function markdownHtml(text: string, scope: ProseScope, marks: Marks, o: ProseOptions): string {
  const extensions = o.literal ? [gfmTable(), gfmStrikethrough(), gfmTaskListItem(), gfmAutolinkLiteral()] : syntaxExtensions();
  const events = postprocess(parse({ extensions }).document().write(preprocess()(text + scope.definitions, undefined, true)));
  markCells(events);
  const htmlExtensions = [gfmTableHtml(), gfmStrikethroughHtml(), gfmTaskListItemHtml(), gfmAutolinkLiteralHtml(), ...(o.literal ? [] : [inlineHtml(scope)])];
  let html = compile({ htmlExtensions, allowDangerousHtml: false, allowDangerousProtocol: false })(events);
  // images are opt-in; links open outside the document
  if (!scope.allowImages) html = html.replace(/<img\b[^>]*\balt="([^"]*)"[^>]*>/g, (_m, alt: string) => `<span class="imd-img" part="image-placeholder">[image: ${alt || "not shown"}]</span>`).replace(/<img\b[^>]*>/g, `<span class="imd-img" part="image-placeholder">[image not shown]</span>`);
  // a web link opens outside the document; in-page and relative links stay where they are
  html = html.replace(/<a href="(https?:[^"]*)"(?![^>]*\brel=)/g, (_m, href: string) => `<a href="${href}" rel="noopener noreferrer" target="_blank"`);
  // code fences: the marked info carries the fence's attribute list
  html = html.replace(/<pre><code(?: class="language-([^"]*)")?>([\s\S]*?)<\/code><\/pre>/g, (_m, cls: string | undefined, code: string) => {
    let lang = cls ?? "";
    let attrs: Attrs = {};
    const mark = CODE_MARK.exec(lang);
    if (mark) { attrs = marks.fences[Number(mark[1])] ?? {}; lang = lang.replace(CODE_MARK, ""); }
    return codeHtml(lang, code, attrs);
  });
  // headings: an explicit {#id} or a generated anchor, shifted by the heading offset
  html = html.replace(/<h([1-6])>([\s\S]*?)<\/h\1>/g, (_m, lvl: string, inner: string) => {
    let attrs: Attrs = {};
    const mark = HEADING_MARK.exec(inner);
    if (mark) { attrs = marks.headings[Number(mark[1])] ?? {}; inner = inner.replace(HEADING_MARK, "").replace(/[ \t]+$/, ""); }
    const level = Math.min(6, Number(lvl) + scope.headingOffset);
    if (o.inline) return `<h${level}>${inner}</h${level}>`;
    const visible = visibleText(inner).trim();
    let id = typeof attrs.id === "string" ? attrs.id : anchor(visible, scope.used);
    if (typeof attrs.id === "string") scope.used.add(id);
    else id = id || "section";
    scope.headings.push({ id, text: visible, level });
    const cls = Array.isArray(attrs.class) && attrs.class.length ? ` class="${e(attrs.class.join(" "))}"` : "";
    return `<h${level} id="${e(id)}"${cls}${styleToken(attrs)}>${inner}</h${level}>`;
  });
  return taskLabels(html).replace(/<pre(?=>)/g, '<pre tabindex="0"');
}

/** Mark the wiki-links inside a table cell: the cell tokenizer has removed one escape layer from their pipes. */
function markCells(events: Event[]): void {
  let cells = 0;
  for (const [phase, token] of events) {
    if (token.type === "tableData" || token.type === "tableHeader") cells += phase === "enter" ? 1 : -1;
    else if (cells && phase === "enter" && (token.type === "imdWiki" || token.type === "imdEmbed")) (token as Token & { _inCell?: boolean })._inCell = true;
  }
}

/** The footnotes, numbered by first reference; definitions no text references come after them. */
export function footnotesHtml(scope: ProseScope): string {
  if (!scope.notes.size) return "";
  const ordered = [...scope.noteOrder.keys()].filter((k) => scope.notes.has(k));
  const rest = [...scope.notes.keys()].filter((k) => !scope.noteOrder.has(k));
  const items = [...ordered, ...rest].map((label) => {
    const back = scope.noteOrder.has(label) ? ` <a class="fnback" href="#fnref-${e(label)}" aria-label="Back to the text">↩</a>` : "";
    const body = proseHtml(scope.notes.get(label)!, scope, { inline: true }).replace(/\n$/, "");
    // the way back sits at the end of the note's last paragraph
    const html = /<\/p>$/.test(body) ? body.replace(/<\/p>$/, `${back}</p>`) : body + back;
    return `<li id="fn-${e(label)}" part="footnote"${scope.noteOrder.has(label) ? "" : ' class="unreferenced"'}>${html}</li>`;
  });
  return `<section class="footnotes" part="footnotes" aria-label="Footnotes"><ol>${items.join("")}</ol></section>`;
}

